import { useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { trackUnsubscribeSuccessPageLoad } from '../tracking/tealium';

import Subscription from '../common/subscription';
import UnsubscribeError from './unsubscribe-error';
import unsubscribeIcon from './img/unsubscribe-icon.png';

import translations from './.vocab';
import { useLanguage, useTranslations } from '@vocab/react';
import PageLoader from '../page-loader';
import { useTypedSelector } from 'src/reducers';
import type { Brand } from '@seek/melways-sites';
import type {
  UpdateNotificationSubscriptionWithTokenMutation,
  UpdateNotificationSubscriptionWithTokenMutationVariables,
} from 'src/types/graphql';
import { updateSubscriptionQL } from 'src/modules/graphql/notifications/preference.graphql';
import { metrics } from '@seek/metrics-js';
import { browserLogError } from 'src/modules/error-logger/browserLog';
import { compatibility } from '@seek/ca-myprofile-utils';

const Unsubscribe = ({ token, brand }: { token: string; brand: Brand }) => {
  const { language: vocabLanguage } = useLanguage();
  const language =
    compatibility.getCompatibleLanguageFromLocaleString(vocabLanguage);

  const [unsubscribeMutation, { error, data }] = useMutation<
    UpdateNotificationSubscriptionWithTokenMutation,
    UpdateNotificationSubscriptionWithTokenMutationVariables
  >(updateSubscriptionQL);

  const locale = useTypedSelector(
    (state) => state.page.pageAnalytics.siteCountry,
  );
  const zone = useTypedSelector((store) => store.page.pageAnalytics.zone);
  const { t } = useTranslations(translations);

  useEffect(() => {
    if (token) {
      unsubscribeMutation({
        variables: {
          input: {
            token,
            subscribe: false,
            locale: `${language}-${locale.toUpperCase()}`,
          },
        },
      }).catch((e) => browserLogError(e));
    }
  }, [unsubscribeMutation, token, language, locale]);

  useEffect(() => {
    if (error) {
      metrics.count('unsubscribe-error-page-rendered');
    } else {
      metrics.count('unsubscribe-page-rendered');
    }
  }, [error]);

  if (error) {
    return <UnsubscribeError language={language} zone={zone} />;
  }

  if (data) {
    const result = data.updateNotificationSubscriptionWithToken;
    const product = result?.product.code;
    const channel = result?.subscriptions[0].channel.code; // always have 1
    trackUnsubscribeSuccessPageLoad(product, channel);

    return (
      <Subscription
        title={t('unsubscribe-subscription-title')}
        cardTitle={t('unsubscribe-subscription-card-title')}
        body={t('unsubscribe-subscription-body')}
        // TODO: only shows for SEEK and jobsdb for now, will be remove when release for all brand.
        body2={
          (brand === 'seek' || brand === 'jobsdb') && product === 'WRU'
            ? t('unsubscribe-subscription-body-2')
            : undefined
        }
        linkMessage={t('unsubscribe-subscription-link-message')}
        icon={unsubscribeIcon}
      />
    );
  }

  return <PageLoader />;
};

export default Unsubscribe;
